import React, { useState, useEffect } from 'react';
import { X, Save, Type, MessageSquare, Tag, Clock, Users, Info, Plus, Ticket } from 'lucide-react';
import { fetchWithAuth } from '../../utils/api'; // Assuming you have this helper
import './EnhancedTemplate.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// It's good practice to define placeholders as constants
const PLACEHOLDERS = {
    code: '{{couponCode}}',
    discount: '{{couponDiscount}}',
    expiry: '{{couponExpiryDate}}',
};

const EnhancedTemplateModal = ({ isOpen, onClose, onSave, editingTemplate, loading, actionOptions, defaultTemplates }) => {
    const [template, setTemplate] = useState(null);
    const [availableCoupons, setAvailableCoupons] = useState([]);
    const [selectedCoupon, setSelectedCoupon] = useState(null);

    // Store the original template content with placeholders to avoid permanent changes
    const [originalContent, setOriginalContent] = useState('');
    const [originalSubject, setOriginalSubject] = useState('');

    const isCouponTemplate = template?.action === 'new_coupon';

    // Fetch coupons when the modal opens for a coupon-related template
    useEffect(() => {
        if (isOpen && isCouponTemplate) {
            fetchWithAuth(`${API_BASE_URL}/api/coupons`)
                .then(setAvailableCoupons)
                .catch(err => console.error("Failed to fetch coupons:", err));
        }
    }, [isOpen, isCouponTemplate]);

    // Initialize/reset state when the editingTemplate or modal visibility changes
    useEffect(() => {
        if (editingTemplate) {
            const initialTemplate = { isActive: true, ...editingTemplate };
            setTemplate(initialTemplate);
            // Capture the original state to allow for previewing without destructive edits
            setOriginalContent(initialTemplate.content || '');
            setOriginalSubject(initialTemplate.subject || '');
            setSelectedCoupon(null);
        }
    }, [editingTemplate, isOpen]);


    if (!isOpen || !template) return null;

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newTemplateValue = type === 'checkbox' ? checked : value;
        setTemplate(prev => ({ ...prev, [name]: newTemplateValue }));
        
        // If the user manually edits the content/subject, update the "original" state
        // so that re-selecting a coupon works as expected on their new text.
        if (name === 'content') {
            setOriginalContent(newTemplateValue);
        }
        if (name === 'subject') {
            setOriginalSubject(newTemplateValue);
        }
    };

    const handleCouponChange = (e) => {
        const couponId = e.target.value;
        const coupon = availableCoupons.find(c => c._id === couponId);
        setSelectedCoupon(coupon);

        if (coupon) {
            // Temporarily replace placeholders with actual data for preview purposes
            const expiryDate = new Date(coupon.expiryDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
            
            const previewSubject = originalSubject
                .replaceAll(PLACEHOLDERS.code, coupon.code)
                .replaceAll(PLACEHOLDERS.discount, `${coupon.discountPercentage}%`)
                .replaceAll(PLACEHOLDERS.expiry, expiryDate);

            const previewContent = originalContent
                .replaceAll(PLACEHOLDERS.code, coupon.code)
                .replaceAll(PLACEHOLDERS.discount, `${coupon.discountPercentage}%`)
                .replaceAll(PLACEHOLDERS.expiry, expiryDate);
            
            setTemplate(prev => ({ ...prev, subject: previewSubject, content: previewContent }));
        } else {
            // If the coupon is deselected, revert to the original content with placeholders
            setTemplate(prev => ({ ...prev, subject: originalSubject, content: originalContent }));
        }
    };
    
    const handleSave = () => {
        // IMPORTANT: Save the template with the original content (placeholders), not the preview data.
        // This ensures the template is reusable for any coupon.
        const finalTemplateData = {
            ...template,
            subject: originalSubject,
            content: originalContent,
        };

        if (onSave) {
            onSave(finalTemplateData);
        }
    };

    const loadDefaultContent = () => {
        const defaultData = defaultTemplates[template.action];
        if (defaultData) {
            const newSubject = defaultData.subject || '';
            const newContent = defaultData.content || '';

            setTemplate(prev => ({
                ...prev,
                subject: newSubject,
                content: newContent,
                variables: defaultData.variables,
            }));
            // Update original state as well
            setOriginalSubject(newSubject);
            setOriginalContent(newContent);

            alert(`Default template for "${template.action}" has been loaded.`);
        } else {
            alert(`No default template found for action: "${template.action}"`);
        }
    };

    const currentActionVariables = actionOptions.find(opt => opt.value === template.action)?.variables || [];
    // For coupon templates, always show the available coupon variables.
    const couponVariables = isCouponTemplate ? [PLACEHOLDERS.code, PLACEHOLDERS.discount, PLACEHOLDERS.expiry] : [];
    const allVariables = [...new Set([...(template.variables || []), ...currentActionVariables, ...couponVariables])];

    return (
         <div className="et-modal-overlay" onClick={onClose}>
            <div className="et-modal-content" onClick={e => e.stopPropagation()}>
                <div className="et-modal-header">
                    <h2>{editingTemplate?._id ? `Edit Template` : 'Create New Template'}</h2>
                    <button onClick={onClose} className="et-close-btn"><X size={24} /></button>
                </div>
                <div className="et-modal-body">
                    {/* ... (Template Name, Action, Description Box) ... unchanged ... */}
                    <div className="et-form-grid">
                        <div className="et-form-group et-grid-span-2">
                            <label htmlFor="name"><Type size={16} /> Template Name *</label>
                            <input id="name" type="text" name="name" value={template.name || ''} onChange={handleInputChange} className="et-input"/>
                        </div>
                        <div className="et-description-box">
                        {/* <Info size={18} /> */}
                        <div>
                            {/* <strong>System Template:</strong> This is a system-defined template used for automated emails. You can customize its content and timing. */}
                            <button onClick={loadDefaultContent} className="et-load-default-btn">Load Default Template</button>
                        </div>
                    </div>
                    </div>
                     {/* <div className="et-form-group">
                        <label htmlFor="action"><Tag size={16} /> Email Action *</label>
                        <select id="action" name="action" value={template.action || ''} onChange={handleInputChange} className="et-input" disabled={!!editingTemplate?._id}>
                            {actionOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                         {editingTemplate?._id && <p className="et-help-text">Action cannot be changed when editing an existing template.</p>}
                    </div> */}

                    {/* <div className="et-description-box">
                        {/* <Info size={18} /> 
                        <div>
                           
                            <button onClick={loadDefaultContent} className="et-load-default-btn">Load Default Template</button>
                        </div>
                    </div> */}


                    <div className="et-form-group">
                        <label htmlFor="subject"><MessageSquare size={16} /> Email Subject *</label>
                        <input id="subject" type="text" name="subject" value={template.subject || ''} onChange={handleInputChange} className="et-input"/>
                    </div>
                    
                    {isCouponTemplate && (
                        <div className="et-coupon-section">
                            <div className="et-form-group">
                                <label htmlFor="coupon-select"><Ticket size={16} /> Apply a Coupon (for Preview)</label>
                                <select id="coupon-select" onChange={handleCouponChange} className="et-input" defaultValue="">
                                    <option value="">-- Select a coupon to populate variables --</option>
                                    {availableCoupons.map(coupon => (
                                        <option key={coupon._id} value={coupon._id}>
                                            {coupon.code} - {coupon.description}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}
                    
                     <div className="et-form-group">
                         <label>Available Variables</label>
                         <div className="et-variables-header">
                            <div className="et-variables-list">
                                {allVariables.map(v => <span key={v} className="et-variable-tag">{v}</span>)}
                            </div>

                            {/* <button className="et-add-variable-btn"><Plus size={14}/> Add Variable</button> */}

                         </div>
                    </div>

                    <div className="et-form-group">
                        <label>Email Content *</label>
                        <textarea
                            name="content" value={template.content || ''} onChange={handleInputChange}
                            className="et-textarea" rows="10" placeholder="Enter your HTML content here, using variables like {{couponCode}}..."/>
                    </div>
                     <div className="et-form-group et-checkbox-group">
                        <input type="checkbox" id="is-active" name="isActive" checked={template.isActive} onChange={handleInputChange} />
                        <label htmlFor="is-active">Template is Active</label>
                    </div>
                </div>
                <div className="et-modal-footer">
                    <button onClick={onClose} className="et-btn et-btn-secondary" disabled={loading}>Cancel</button>
                    <button onClick={handleSave} className="et-btn et-btn-primary" disabled={loading}>
                        <Save size={16} /> {loading ? 'Saving...' : 'Save Template'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EnhancedTemplateModal;